import React, { useState } from 'react';
import { Button, TextField, Typography, Link as MuiLink, IconButton } from '@mui/material';
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from 'firebase/firestore'; 
import { auth, db } from '../../firebase/firebase.js'; 
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MagnetLines from '../../components/magnetLines/MagnetLines';
import Loader from '../../components/loader/Loader';
import './authSass/Register.scss';

const Register = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmSetPassword] = useState(false); 
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!firstName.trim() || !lastName.trim()) {
      toast.error('First Name and Last Name are required!');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }

    setIsLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const fullDisplayName = `${firstName.trim()} ${lastName.trim()}`;
      await updateProfile(user, {
        displayName: fullDisplayName, 
      });
      console.log(`[Register] User Auth profile updated: displayName = ${fullDisplayName}`);
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        displayName: fullDisplayName,
        role: "user", 
        createdAt: serverTimestamp(), 
        lastLoginAt: serverTimestamp(), 
      });
      console.log(`[Register] User document created in Firestore for UID: ${user.uid} with role: "user"`);


      toast.success("Registration Successful! You are now logged in.");
      navigate("/"); 
    } catch (error) {
      console.error("Registration error: ", error);
      if (error.code === 'auth/email-already-in-use') {
        toast.error('This email is already registered.');
      } else if (error.code === 'auth/weak-password') {
        toast.error('Password should be at least 6 characters.');
      } else {
        toast.error(`Registration failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <Loader />}
      <div className="register-page-wrapper">
        <MagnetLines
          rows={9}
          columns={9}
          lineColor="rgba(80, 180, 255, 0.4)"
          lineWidth="0.6vmin"
          lineHeight="4vmin"
          baseAngle={-45}
          fullscreen={true}
        />

        <div className="register-container">
          <form onSubmit={handleSubmit} className="register-form">
            <Typography variant="h4">Register</Typography>
            <TextField
              label="First Name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
              margin="normal"
              variant="outlined"
              required 
              autoComplete="given-name"
            />
          
            <TextField
              label="Last Name"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              margin="normal"
              variant="outlined"
              required 
              autoComplete="family-name"
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              margin="normal"
              variant="outlined"
              autoComplete="email"
            />
            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              margin="normal"
              variant="outlined"
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    aria-label="toggle password visibility"
                    sx={{ color: 'text.secondary' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
            <TextField
              label="Confirm Password"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              margin="normal"
              variant="outlined"
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <IconButton
                    
                    onClick={() => setShowConfirmSetPassword(!showConfirmPassword)}
                    edge="end"
                    aria-label="toggle confirm password visibility"
                    sx={{ color: 'text.secondary' }}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
            <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>Register</Button>

            <Typography variant="body2" style={{ marginTop: '10px' }}>
              Already have an account?
              <MuiLink component={RouterLink} to="/login" sx={{ ml: 0.5 }}>Login</MuiLink>
            </Typography>
          </form>
        </div>
      </div>
    </>
  );
};

export default Register;
